'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { LuPlus } from 'react-icons/lu';
import { MdOutlineAccessTime, MdOutlineLocalPhone, MdOutlineLocationOn } from 'react-icons/md';
import { RiMenuFill } from 'react-icons/ri';

import { OPENING_HOURS } from '../../constants/opening-hours';
import { formatContactAddress } from '../../utils/contact-address';

/**
 * Nested (layer >= 1) items keep the pre-existing sub-menu treatment — Figma 429:49212 only shows
 * the sidebar collapsed, so there's no design reference for the expanded state to redo.
 */
const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;
  const isRoot = !layer;

  return (
    <AccordionItem
      className={cn('flex w-full flex-col border-none', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <div
        className={cn(
          'flex w-full items-center justify-between gap-2 py-1',
          isRoot && 'py-0',
          'ea-mobile-sidebar__category-row',
        )}
      >
        {/* `SheetClose asChild` so the row is a single link — a <button> nested inside an <a>
            is invalid HTML and is announced as two controls. */}
        <SheetClose asChild>
          <Link
            href={isRoot ? ROUTES.CATEGORY({ slug: category.slug }) : category.slug}
            prefetchOnHover
            className={cn(
              'block min-w-0 flex-1 text-left',
              isRoot && 'text-[18px] font-medium leading-[1.3] text-black',
              layer === 1 && 'text-sm font-medium text-black',
              layer && layer > 1 && 'text-xs font-normal text-black',
            )}
          >
            {category.name}
          </Link>
        </SheetClose>
        {hasChildren && (
          <AccordionTrigger
            hideIcon
            aria-label={category.name}
            // `AccordionTrigger`'s own base class already carries a hardcoded
            // `[&[data-state=open]>svg]:rotate-180` for its default chevron. Since that's an
            // arbitrary-variant selector, `cn()`'s tailwind-merge dedup doesn't recognize it as
            // conflicting with a differently-valued one here, so cascade order (not JSX order)
            // would otherwise decide the winner. `!rotate-45` forces this one to win instead of
            // depending on that.
            className="w-auto flex-none p-0 hover:no-underline [&[data-state=open]>svg]:!rotate-45"
          >
            <LuPlus
              size={isRoot ? 14 : 12}
              className="shrink-0 text-black transition-transform duration-200"
              aria-hidden
            />
          </AccordionTrigger>
        )}
      </div>
      <AccordionContent className="pb-0">
        <Accordion type="single" collapsible className={cn(isRoot && 'mt-3 flex flex-col gap-2')}>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  categories?: CategoriesEntity[];
  whiteText?: boolean;
};

/**
 * Rebuilt 1:1 from the Figma "Mobile Transparent header" sidebar frame (429:49212): a 52px close
 * bar, the category list, a static link block (Realizácie / Nábytok na mieru / Kontakt — the same
 * `top_bar` CMS menu the desktop topbar used to render), the "Vernostný program" link (not backed
 * by any CMS menu, so hardcoded via `t('text572')`), and, pinned to the bottom, the showroom
 * address and phone.
 */
const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  const categories = props.categories ?? [];
  const contact = generalLayout.contact;
  const phone = contact?.phone;
  const address = formatContactAddress(contact);

  return (
    <Sheet>
      <SheetTrigger
        aria-label={t('text176')}
        className={cn(
          props.whiteText ? 'text-white' : 'text-black',
          'ea-mobile-sidebar__trigger',
          props.className?.trigger,
        )}
      >
        <RiMenuFill className="size-6" />
      </SheetTrigger>
      <SheetContent
        side={props.side}
        className={cn(
          'flex w-[85%] max-w-[360px] flex-col gap-0 overflow-hidden bg-white p-0',
          'ea-mobile-sidebar__content',
          props.className?.content,
        )}
        closeIcon={{ wrapper: 'left-4 top-[14px]', icon: 'size-6 text-black' }}
      >
        {/* Empty 52px strip the close button (positioned by SheetContent) floats over. */}
        <div className="h-[52px] w-full shrink-0" />

        <div className="flex min-h-0 flex-1 flex-col justify-between gap-8 overflow-y-auto px-4 py-8">
          <div className="flex w-full flex-col gap-4">
            <Accordion type="single" collapsible className="flex w-full flex-col gap-6">
              {categories.map((category, index) => (
                <CategoryItem key={index} category={category} />
              ))}
            </Accordion>

            <div className="flex w-full flex-col gap-6">
              <div className="h-px w-full bg-[#E5E7EB]" />
              {props.menu?.map((item, index) => {
                const href = item.path ? getSafeHref(item.path) : '#';
                const Comp = item.path && isRelativeHref(href) ? Link : 'a';
                return (
                  <SheetClose key={index} asChild>
                    <Comp
                      href={href}
                      prefetch={false}
                      className="text-[16px] leading-[1.3] text-black"
                    >
                      {item.name}
                    </Comp>
                  </SheetClose>
                );
              })}
              <SheetClose asChild>
                <Link
                  prefetchOnHover
                  href={ROUTES.LOYALTY_PROGRAM()}
                  prefetch={false}
                  className="text-[16px] font-bold leading-[1.3] tracking-[-0.16px] text-[#7B3F0C]"
                >
                  {t('text572')}
                </Link>
              </SheetClose>
            </div>
          </div>

          <div className="flex w-full flex-col gap-8">
            <div className="h-px w-full bg-[#E5E7EB]" />
            <div className="flex w-full flex-col gap-4">
              {!!address && (
                <div className="flex items-center gap-2">
                  <MdOutlineLocationOn size={18} className="shrink-0 text-black" aria-hidden />
                  <span className="text-[14px] leading-[1.3] text-black">{address}</span>
                </div>
              )}
              {!!phone && (
                <div className="flex items-center gap-1.5">
                  <MdOutlineLocalPhone size={18} className="shrink-0 text-black" aria-hidden />
                  <span className="text-[14px] leading-[1.3] text-black">{t('text1910')}:</span>
                  <a
                    href={`tel:${phone.replace(/\s+/g, '')}`}
                    className="text-[14px] font-bold leading-[1.3] text-black"
                  >
                    {phone}
                  </a>
                </div>
              )}
              <div className="flex items-start gap-2">
                <MdOutlineAccessTime size={18} className="shrink-0 text-black" aria-hidden />
                <div className="flex w-full flex-col gap-1">
                  {OPENING_HOURS.map(({ dayKeys, hours }) => (
                    <div
                      key={dayKeys[0]}
                      className="flex w-full items-center justify-between gap-2 text-[14px] leading-[1.3] text-black"
                    >
                      <span>{dayKeys.map(key => t(key)).join(' – ')}</span>
                      <span>{hours ?? t('text2374')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileSidebar;
